import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'i18n/TranslationProvider';
import { MdStar } from 'react-icons/md';
import { PageShell, PageHeader } from 'components/layout/PageShell';
import {
  UnifiedDataTableShell,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';
import API from 'api/api';

function StarRating({ value }) {
  const starColor = useColorModeValue('yellow.400', 'yellow.300');
  const count = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));

  return (
    <HStack spacing={0.5}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          as={MdStar}
          color={i < count ? starColor : 'gray.300'}
          boxSize={4}
        />
      ))}
    </HStack>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function AdminReviews() {
  const { t } = useTranslation();
  const toast = useToast();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const { tableSx } = useUnifiedTableStyles();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/reviews');
      const data = res?.data?.data ?? res?.data ?? [];
      setReviews(Array.isArray(data) ? data : data.reviews ?? []);
    } catch {
      toast({ title: t('Failed to load reviews'), status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleRemove = async (id) => {
    setRemovingId(id);
    try {
      await API.delete(`/reviews/${id}`);
      toast({ title: t('Review removed'), status: 'success', duration: 2500 });
      setReviews((prev) => prev.filter((r) => (r._id || r.id) !== id));
    } catch {
      toast({ title: t('Failed to remove review'), status: 'error', duration: 3000 });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={t('Reviews')}
          description={t('All partner and transport reviews submitted by clients.')}
        />

        <UnifiedSurfaceCard p={5}>
          <UnifiedDataTableShell
            loading={loading}
            loadingLabel={t('Loading reviews...')}
            isEmpty={reviews.length === 0}
            emptyTitle={t('No reviews found')}
            emptyDescription={t('Client feedback will appear here when reviews are submitted.')}
          >
              <Table size="sm" variant="simple" sx={tableSx}>
                <Thead>
                  <Tr>
                    <Th>#</Th>
                    <Th>{t('Stars')}</Th>
                    <Th>{t('Partner')}</Th>
                    <Th>{t('Client')}</Th>
                    <Th>{t('Comment')}</Th>
                    <Th>{t('Listing')}</Th>
                    <Th>{t('Date')}</Th>
                    <Th>{t('Actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {reviews.map((review, index) => {
                    const id = review._id || review.id;
                    const partnerName =
                      review.partner?.name || review.partnerName || '—';
                    const clientName =
                      review.client?.name || review.clientName || review.authorName || '—';
                    const listingRef =
                      review.listing?.title ||
                      review.listing?.humanId ||
                      review.listingId ||
                      '—';

                    return (
                      <Tr key={id}>
                        <Td color={mutedText} fontSize="xs">
                          {index + 1}
                        </Td>
                        <Td>
                          <StarRating value={review.rating ?? review.stars} />
                        </Td>
                        <Td fontWeight="600">{partnerName}</Td>
                        <Td>{clientName}</Td>
                        <Td maxW="220px" isTruncated color={mutedText}>
                          {review.comment || review.text || '—'}
                        </Td>
                        <Td>{listingRef}</Td>
                        <Td color={mutedText} fontSize="xs">
                          {formatDate(review.createdAt)}
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="red"
                            isLoading={removingId === id}
                            isDisabled={!!removingId}
                            onClick={() => handleRemove(id)}
                          >
                            {t('Remove')}
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
          </UnifiedDataTableShell>
        </UnifiedSurfaceCard>
      </VStack>
    </PageShell>
  );
}
